import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TEuDdpStore,
	TEuDdpDeliveryOperatorSearchRequest,
	TEuDdpDeliveryOperatorsSearchResponse,
	TEuDdpCreateDeliveryOperatorRequest,
	TEuDdpUpdateDeliveryOperatorRequest,
	TEuDdpTariffSearchRequest,
	TEuDdpTariffsSearchResponse,
	TEuDdpCreateTariffRequest,
	TEuDdpUpdateTariffRequest,
	TEuDdpPricingPlanSearchRequest,
	TEuDdpPricingPlansSearchResponse,
	TEuDdpCreatePricingPlanRequest,
	TEuDdpUpdatePricingPlanRequest,
	TEuDdpRecordSearchRequest,
	TEuDdpRecordsSearchResponse,
	TEuDdpOrderSearchRequest,
	TEuDdpOrdersSearchResponse,
	TEuDdpOrderDetailsRequest,
	TEuDdpOrderDetailsResponse,
	TEuDdpLabelsResponse,
	TEuDdpOmnivaLabelRequest,
	TEuDdpInvoiceProformaRequest,
} from './types';
import { EU_DDP_SLICE_NAME } from './constants';

const initialState: TEuDdpStore = {
	deliveryOperatorsSearch: null,
	deliveryOperatorsSearchLoading: false,
	createDeliveryOperatorLoading: false,
	createDeliveryOperatorSuccess: false,
	updateDeliveryOperatorLoading: false,
	updateDeliveryOperatorSuccess: false,
	tariffsSearch: null,
	tariffsSearchLoading: false,
	createTariffLoading: false,
	createTariffSuccess: false,
	updateTariffLoading: false,
	updateTariffSuccess: false,
	pricingPlansSearch: null,
	pricingPlansSearchLoading: false,
	createPricingPlanLoading: false,
	createPricingPlanSuccess: false,
	updatePricingPlanLoading: false,
	updatePricingPlanSuccess: false,
	euDdpRecordsSearch: null,
	euDdpRecordsSearchLoading: false,
	ordersSearch: null,
	ordersSearchLoading: false,
	orderDetails: null,
	orderDetailsLoading: false,
	printLabels: null,
	printLabelsLoading: false,
	omnivaLabelLoading: false,
	invoiceProformaLoading: false,
};

const euDdpSlice = createSlice({
	name: EU_DDP_SLICE_NAME,
	initialState,
	reducers: {
		searchDeliveryOperatorsRequest: (
			state,
			_action: PayloadAction<TEuDdpDeliveryOperatorSearchRequest>,
		) => {
			state.deliveryOperatorsSearchLoading = true;
		},
		searchDeliveryOperatorsSuccess: (
			state,
			action: PayloadAction<TEuDdpDeliveryOperatorsSearchResponse>,
		) => {
			state.deliveryOperatorsSearch = action.payload;
			state.deliveryOperatorsSearchLoading = false;
		},
		searchDeliveryOperatorsFailure: (state) => {
			state.deliveryOperatorsSearchLoading = false;
		},
		clearDeliveryOperatorsSearch: (state) => {
			state.deliveryOperatorsSearch = null;
		},

		createDeliveryOperatorRequest: (
			state,
			_action: PayloadAction<TEuDdpCreateDeliveryOperatorRequest>,
		) => {
			state.createDeliveryOperatorLoading = true;
			state.createDeliveryOperatorSuccess = false;
		},
		createDeliveryOperatorSuccess: (state) => {
			state.createDeliveryOperatorLoading = false;
			state.createDeliveryOperatorSuccess = true;
		},
		createDeliveryOperatorFailure: (state) => {
			state.createDeliveryOperatorLoading = false;
		},
		resetCreateDeliveryOperatorSuccess: (state) => {
			state.createDeliveryOperatorSuccess = false;
		},

		updateDeliveryOperatorRequest: (
			state,
			_action: PayloadAction<TEuDdpUpdateDeliveryOperatorRequest>,
		) => {
			state.updateDeliveryOperatorLoading = true;
			state.updateDeliveryOperatorSuccess = false;
		},
		updateDeliveryOperatorSuccess: (state) => {
			state.updateDeliveryOperatorLoading = false;
			state.updateDeliveryOperatorSuccess = true;
		},
		updateDeliveryOperatorFailure: (state) => {
			state.updateDeliveryOperatorLoading = false;
		},
		resetUpdateDeliveryOperatorSuccess: (state) => {
			state.updateDeliveryOperatorSuccess = false;
		},

		searchTariffsRequest: (state, _action: PayloadAction<TEuDdpTariffSearchRequest>) => {
			state.tariffsSearchLoading = true;
		},
		searchTariffsSuccess: (state, action: PayloadAction<TEuDdpTariffsSearchResponse>) => {
			state.tariffsSearch = action.payload;
			state.tariffsSearchLoading = false;
		},
		searchTariffsFailure: (state) => {
			state.tariffsSearchLoading = false;
		},
		clearTariffsSearch: (state) => {
			state.tariffsSearch = null;
		},

		createTariffRequest: (state, _action: PayloadAction<TEuDdpCreateTariffRequest>) => {
			state.createTariffLoading = true;
			state.createTariffSuccess = false;
		},
		createTariffSuccess: (state) => {
			state.createTariffLoading = false;
			state.createTariffSuccess = true;
		},
		createTariffFailure: (state) => {
			state.createTariffLoading = false;
		},
		resetCreateTariffSuccess: (state) => {
			state.createTariffSuccess = false;
		},

		updateTariffRequest: (state, _action: PayloadAction<TEuDdpUpdateTariffRequest>) => {
			state.updateTariffLoading = true;
			state.updateTariffSuccess = false;
		},
		updateTariffSuccess: (state) => {
			state.updateTariffLoading = false;
			state.updateTariffSuccess = true;
		},
		updateTariffFailure: (state) => {
			state.updateTariffLoading = false;
		},
		resetUpdateTariffSuccess: (state) => {
			state.updateTariffSuccess = false;
		},

		searchPricingPlansRequest: (state, _action: PayloadAction<TEuDdpPricingPlanSearchRequest>) => {
			state.pricingPlansSearchLoading = true;
		},
		searchPricingPlansSuccess: (state, action: PayloadAction<TEuDdpPricingPlansSearchResponse>) => {
			state.pricingPlansSearch = action.payload;
			state.pricingPlansSearchLoading = false;
		},
		searchPricingPlansFailure: (state) => {
			state.pricingPlansSearchLoading = false;
		},
		clearPricingPlansSearch: (state) => {
			state.pricingPlansSearch = null;
		},

		createPricingPlanRequest: (state, _action: PayloadAction<TEuDdpCreatePricingPlanRequest>) => {
			state.createPricingPlanLoading = true;
			state.createPricingPlanSuccess = false;
		},
		createPricingPlanSuccess: (state) => {
			state.createPricingPlanLoading = false;
			state.createPricingPlanSuccess = true;
		},
		createPricingPlanFailure: (state) => {
			state.createPricingPlanLoading = false;
		},
		resetCreatePricingPlanSuccess: (state) => {
			state.createPricingPlanSuccess = false;
		},

		updatePricingPlanRequest: (state, _action: PayloadAction<TEuDdpUpdatePricingPlanRequest>) => {
			state.updatePricingPlanLoading = true;
			state.updatePricingPlanSuccess = false;
		},
		updatePricingPlanSuccess: (state) => {
			state.updatePricingPlanLoading = false;
			state.updatePricingPlanSuccess = true;
		},
		updatePricingPlanFailure: (state) => {
			state.updatePricingPlanLoading = false;
		},
		resetUpdatePricingPlanSuccess: (state) => {
			state.updatePricingPlanSuccess = false;
		},

		searchEuDdpRecordsRequest: (state, _action: PayloadAction<TEuDdpRecordSearchRequest>) => {
			state.euDdpRecordsSearchLoading = true;
		},
		searchEuDdpRecordsSuccess: (state, action: PayloadAction<TEuDdpRecordsSearchResponse>) => {
			state.euDdpRecordsSearch = action.payload;
			state.euDdpRecordsSearchLoading = false;
		},
		searchEuDdpRecordsFailure: (state) => {
			state.euDdpRecordsSearchLoading = false;
		},
		clearEuDdpRecordsSearch: (state) => {
			state.euDdpRecordsSearch = null;
		},

		searchOrdersRequest: (state, _action: PayloadAction<TEuDdpOrderSearchRequest>) => {
			state.ordersSearchLoading = true;
		},
		searchOrdersSuccess: (state, action: PayloadAction<TEuDdpOrdersSearchResponse>) => {
			state.ordersSearch = action.payload;
			state.ordersSearchLoading = false;
		},
		searchOrdersFailure: (state) => {
			state.ordersSearchLoading = false;
		},
		clearOrdersSearch: (state) => {
			state.ordersSearch = null;
		},

		getOrderDetailsRequest: (state, _action: PayloadAction<TEuDdpOrderDetailsRequest>) => {
			state.orderDetailsLoading = true;
		},
		getOrderDetailsSuccess: (state, action: PayloadAction<TEuDdpOrderDetailsResponse>) => {
			state.orderDetails = action.payload;
			state.orderDetailsLoading = false;
		},
		getOrderDetailsFailure: (state) => {
			state.orderDetailsLoading = false;
		},
		clearOrderDetails: (state) => {
			state.orderDetails = null;
		},

		getPrintLabelsRequest: (state) => {
			state.printLabelsLoading = true;
		},
		getPrintLabelsSuccess: (state, action: PayloadAction<TEuDdpLabelsResponse>) => {
			state.printLabels = action.payload;
			state.printLabelsLoading = false;
		},
		getPrintLabelsFailure: (state) => {
			state.printLabelsLoading = false;
		},
		clearPrintLabels: (state) => {
			state.printLabels = null;
		},

		getOmnivaLabelRequest: (state, _action: PayloadAction<TEuDdpOmnivaLabelRequest>) => {
			state.omnivaLabelLoading = true;
		},
		getOmnivaLabelSuccess: (state) => {
			state.omnivaLabelLoading = false;
		},
		getOmnivaLabelFailure: (state) => {
			state.omnivaLabelLoading = false;
		},

		getInvoiceProformaRequest: (state, _action: PayloadAction<TEuDdpInvoiceProformaRequest>) => {
			state.invoiceProformaLoading = true;
		},
		getInvoiceProformaSuccess: (state) => {
			state.invoiceProformaLoading = false;
		},
		getInvoiceProformaFailure: (state) => {
			state.invoiceProformaLoading = false;
		},
	},
});

export const {
	searchDeliveryOperatorsRequest,
	searchDeliveryOperatorsSuccess,
	searchDeliveryOperatorsFailure,
	clearDeliveryOperatorsSearch,
	createDeliveryOperatorRequest,
	createDeliveryOperatorSuccess,
	createDeliveryOperatorFailure,
	resetCreateDeliveryOperatorSuccess,
	updateDeliveryOperatorRequest,
	updateDeliveryOperatorSuccess,
	updateDeliveryOperatorFailure,
	resetUpdateDeliveryOperatorSuccess,
	searchTariffsRequest,
	searchTariffsSuccess,
	searchTariffsFailure,
	clearTariffsSearch,
	createTariffRequest,
	createTariffSuccess,
	createTariffFailure,
	resetCreateTariffSuccess,
	updateTariffRequest,
	updateTariffSuccess,
	updateTariffFailure,
	resetUpdateTariffSuccess,
	searchPricingPlansRequest,
	searchPricingPlansSuccess,
	searchPricingPlansFailure,
	clearPricingPlansSearch,
	createPricingPlanRequest,
	createPricingPlanSuccess,
	createPricingPlanFailure,
	resetCreatePricingPlanSuccess,
	updatePricingPlanRequest,
	updatePricingPlanSuccess,
	updatePricingPlanFailure,
	resetUpdatePricingPlanSuccess,
	searchEuDdpRecordsRequest,
	searchEuDdpRecordsSuccess,
	searchEuDdpRecordsFailure,
	clearEuDdpRecordsSearch,
	searchOrdersRequest,
	searchOrdersSuccess,
	searchOrdersFailure,
	clearOrdersSearch,
	getOrderDetailsRequest,
	getOrderDetailsSuccess,
	getOrderDetailsFailure,
	clearOrderDetails,
	getPrintLabelsRequest,
	getPrintLabelsSuccess,
	getPrintLabelsFailure,
	clearPrintLabels,
	getOmnivaLabelRequest,
	getOmnivaLabelSuccess,
	getOmnivaLabelFailure,
	getInvoiceProformaRequest,
	getInvoiceProformaSuccess,
	getInvoiceProformaFailure,
} = euDdpSlice.actions;

export default euDdpSlice.reducer;
